/**
 * HtmlEditor for help content with the help toolbar. The console's HtmlEditor fires onChange when
 * it mounts or gets a new value (it normalises the HTML), so a change that arrives before the
 * person has typed or clicked in the editor, shortly after the value was set from outside, is
 * flagged `absorb`. Editors apply absorbed changes to their saved baseline too, so opening or
 * saving a page never shows "unsaved changes".
 */

import { useEffect, useRef } from 'react';
import { HtmlEditor } from '@/components/ui';
import { HELP_EDITOR_TOOLBAR } from './helpAdminUtils';

const ABSORB_MS = 1500;

interface HelpHtmlFieldProps {
  label?: string;
  description?: string;
  placeholder?: string;
  value: string;
  onChange: (html: string, absorb: boolean) => void;
  minHeight?: number;
  disabled?: boolean;
}

export function HelpHtmlField({ label, description, placeholder, value, onChange, minHeight = 140, disabled }: HelpHtmlFieldProps) {
  const externalAt = useRef(Date.now());
  const lastEmitted = useRef<string | null>(null);
  const touched = useRef(false);

  useEffect(() => {
    if (value !== lastEmitted.current) {
      externalAt.current = Date.now();
      touched.current = false;
    }
  }, [value]);

  const markTouched = () => {
    touched.current = true;
  };

  return (
    <div onKeyDown={markTouched} onMouseDown={markTouched} onPaste={markTouched}>
      <HtmlEditor
        label={label}
        description={description}
        placeholder={placeholder}
        value={value}
        onChange={(html) => {
          lastEmitted.current = html;
          onChange(html, !touched.current && Date.now() - externalAt.current < ABSORB_MS);
        }}
        minHeight={minHeight}
        maxHeight={420}
        toolbarFeatures={HELP_EDITOR_TOOLBAR}
        disabled={disabled}
      />
    </div>
  );
}
